'use strict'
const c = require('./alertPreferenceCatalog')
const clone = value => JSON.parse(JSON.stringify(value))
function mergeEvents(loaded = {}) {
  const events = clone(c.DEFAULT_EVENTS)
  for (const [kind, settings] of Object.entries(loaded))
    events[kind] = events[kind] ? { ...events[kind], ...settings } : settings
  return events
}
function channelEnabled(state, channel) {
  if (channel === 'sms')
    return state.channels.sms === true && state.globalOverrides?.lockedChannels?.sms !== true
  if (state.globalOverrides?.forcedChannels?.[channel] === true) return true
  if (state.globalOverrides?.lockedChannels?.[channel] === true) return false
  return state.channels[channel] !== false
}
function channelLocked(state, channel) {
  if (channel === 'sms') return state.globalOverrides?.lockedChannels?.sms === true
  return (
    state.globalOverrides?.forcedChannels?.[channel] === true ||
    state.globalOverrides?.lockedChannels?.[channel] === true
  )
}
function eventEnabled(state, kind, channel) {
  if (c.CHANNEL_NOT_APPLICABLE[kind]?.includes(channel)) return null
  if (channel === 'sms')
    return (
      state.events[kind]?.sms === true &&
      state.globalOverrides?.lockedChannels?.sms !== true &&
      state.globalOverrides?.lockedEvents?.[kind]?.sms !== true &&
      state.globalOverrides?.disabledEvents?.[kind] !== true
    )
  if (kind === 'buildPartnerJobArrived' && state.events[kind]?.[channel] !== true) return false
  if (state.globalOverrides?.forcedEvents?.[kind]?.[channel] === true) return true
  if (state.globalOverrides?.lockedEvents?.[kind]?.[channel] === true) return false
  if (state.globalOverrides?.disabledEvents?.[kind]) return false
  const settings = state.events[kind],
    value = settings?.[channel]
  if (value === true) return true
  if (value === false) return false
  if (!settings) return c.DEFAULT_EVENTS[kind]?.[channel] ?? false
  return state.channels[channel] !== false
}
// What the person receives on one channel for one kind: the event's setting through the master switch. An
// administrator's forced event reaches a person whose master switch is off; a disabled event reaches no one. Alert
// delivery decides with this (utils/alertDelivery.js resolveChannels), so what Settings and the app show is what is sent.
function channelDelivers(state, kind, channel) {
  if (state.globalOverrides?.disabledEvents?.[kind] === true) return false
  if (eventEnabled(state, kind, channel) !== true) return false
  if (channel === 'sms') return channelEnabled(state, 'sms')
  return (
    channelEnabled(state, channel) || state.globalOverrides?.forcedEvents?.[kind]?.[channel] === true
  )
}
function eventLocked(state, kind, channel) {
  if (channel === 'sms')
    return (
      state.globalOverrides?.lockedEvents?.[kind]?.sms === true ||
      state.globalOverrides?.lockedChannels?.sms === true
    )
  return (
    state.globalOverrides?.forcedEvents?.[kind]?.[channel] === true ||
    state.globalOverrides?.lockedEvents?.[kind]?.[channel] === true
  )
}
// The rows the old notificationPrefs format sets: each mode a person picked, as a full row (their choices).
function legacyChoices(old = {}) {
  const mode = value =>
    !value || value === 'off'
      ? {
          badge: false,
          dropdown: false,
          hub: true,
          overlay: false,
          email: false,
          push: false,
          sound: false,
        }
      : {
          badge: value.includes('bell'),
          dropdown: value.includes('bell'),
          hub: true,
          overlay: value.includes('overlay'),
          email: value.includes('email'),
          push: value.includes('push'),
          sound: false,
        }
  const groups = {
    comms: {
      unreadText: 'unreadCommsMode',
      unreadEmail: 'unreadCommsMode',
      noteTagged: 'notesTaggedMode',
      noteAll: 'notesAllMode',
    },
    lifecycle: {
      customerCompleted: 'customerCompletedMode',
      customerCanceled: 'customerCanceledMode',
      customerOnHold: 'customerOnHoldMode',
      customerReleasedFromHold: 'customerReleasedFromHoldMode',
    },
    tasks: { taskRejected: 'taskRejectedMode' },
    leadPool: { leadPool: 'mode' },
  }
  const rows = {}
  for (const [group, keys] of Object.entries(groups))
    if (old[group])
      for (const [kind, key] of Object.entries(keys)) rows[kind] = mode(old[group][key])
  return rows
}
// The old notificationPrefs format over the defaults, as Settings shows it.
function legacyEvents(old = {}) {
  const events = clone(c.DEFAULT_EVENTS)
  for (const [kind, row] of Object.entries(legacyChoices(old)))
    events[kind] = { ...events[kind], ...row }
  return events
}
// A saved document holds only the person's choices; the catalog supplies everything else when it is read
// (docs/bugs/alert_defaults_two_lists_BUG.md). Settings once saved every row, defaults included, so a value equal to the
// default is a copy, not a choice. choicesOnly keeps what differs from the default plus the fields in `keep` (paths like
// 'events.unreadText.push' the person is known to have set); reading the result gives what reading the document gives.
function choicesOnly(saved = {}, keep = new Set()) {
  const out = {}
  if (saved.channels) {
    out.channels = {}
    for (const [channel, value] of Object.entries(saved.channels))
      if (value !== c.DEFAULT_CHANNELS[channel] || keep.has(`channels.${channel}`))
        out.channels[channel] = value
  }
  if (saved.events) {
    out.events = {}
    for (const [kind, row] of Object.entries(saved.events))
      for (const [channel, value] of Object.entries(row || {}))
        if (value !== c.DEFAULT_EVENTS[kind]?.[channel] || keep.has(`events.${kind}.${channel}`))
          out.events[kind] = { ...(out.events[kind] || {}), [channel]: value }
  }
  if (saved.consolidation) {
    out.consolidation = {}
    for (const [kind, row] of Object.entries(saved.consolidation)) {
      const fallback = c.DEFAULT_CONSOLIDATION[kind]
      if (!fallback || row?.enabled !== fallback.enabled || row?.after !== fallback.after)
        out.consolidation[kind] = row
    }
  }
  return out
}
// The choices a writer starts from: a current-format document's own; for none, or one read as the old format, the modes
// the person picked in the old notificationPrefs format (the old document itself holds no current choice).
function savedChoices(existing, old = {}) {
  if (existing?._version >= 2)
    return clone({
      channels: existing.channels || {},
      events: existing.events || {},
      consolidation: existing.consolidation || {},
    })
  return { channels: {}, events: legacyChoices(old), consolidation: {} }
}
// One choice laid over others, field by field ({ events: { kind: { channel: value } } } and the like).
function mergeChoices(base = {}, choice = {}) {
  const out = clone(base)
  for (const [top, value] of Object.entries(choice)) {
    if (top === 'channels') out.channels = { ...(out.channels || {}), ...value }
    else {
      out[top] = out[top] || {}
      for (const [kind, row] of Object.entries(value)) out[top][kind] = { ...(out[top][kind] || {}), ...row }
    }
  }
  return out
}
// Same v2 migration and v3 merge rules as the CRM store. Reads never persist a migration.
function loadPreferences(existing, old = {}) {
  const modern = existing?._version >= c.SCHEMA_VERSION,
    v2 = existing?._version === 2
  return {
    channels: { ...c.DEFAULT_CHANNELS, ...(modern || v2 ? existing.channels || {} : {}) },
    events: modern || v2 ? mergeEvents(existing.events || {}) : legacyEvents(old),
    consolidation: {
      ...clone(c.DEFAULT_CONSOLIDATION),
      ...(modern ? existing.consolidation || {} : {}),
    },
  }
}
module.exports = {
  mergeEvents,
  channelEnabled,
  channelLocked,
  eventEnabled,
  channelDelivers,
  eventLocked,
  legacyEvents,
  legacyChoices,
  loadPreferences,
  choicesOnly,
  savedChoices,
  mergeChoices,
}
